import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import FlashcardCard from '../components/FlashcardCard';
import { getFlashcards } from '../data/flashcardsProvider';

const NewYorkRealEstateExam: React.FC = () => {
  const [showAnswer, setShowAnswer] = useState(false);
  const flashcards = getFlashcards('ny');
  const sampleCard = flashcards[0];

  const handleCardClick = () => {
    setShowAnswer(!showAnswer);
  };

  return (
    <>
      <Helmet>
        <title>New York Real Estate Exam Preparation | RealEstart.io</title>
        <meta 
          name="description" 
          content="Prepare for your New York real estate exam with 557 flashcards. Cover Article 12-A, NY license law, agency, contracts, and national real estate principles." 
        />
        <meta name="keywords" content="New York real estate exam, NY real estate license, Article 12-A, real estate flashcards, practice test" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://realestart.io/new-york-real-estate-exam" />
        <meta property="og:title" content="New York Real Estate Exam Preparation | RealEstart.io" />
        <meta property="og:description" content="Prepare for your New York real estate exam with 557 flashcards. Cover Article 12-A, NY license law, agency, contracts, and national real estate principles." />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://realestart.io/new-york-real-estate-exam" />
        <meta property="twitter:title" content="New York Real Estate Exam Preparation | RealEstart.io" />
        <meta property="twitter:description" content="Prepare for your New York real estate exam with 557 flashcards. Cover Article 12-A, NY license law, agency, contracts, and national real estate principles." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOccupationalCredential",
            "name": "New York Real Estate License",
            "description": "New York real estate salesperson license preparation",
            "provider": {
              "@type": "Organization",
              "name": "RealEstart.io",
              "url": "https://realestart.io"
            },
            "educationalLevel": "Professional",
            "competencyRequired": "New York Department of State, Article 12-A",
            "credentialCategory": "License"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">R</span>
                </div>
                <span className="text-xl font-bold text-gray-900">RealEstart.io</span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link to="/auth" className="text-gray-600 hover:text-blue-600 transition-colors">Sign In</Link>
                <Link to="/checkout?plan=trial" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg">
                  Start Free Trial
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-6">
                New York Real Estate Exam
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Ace Your New York Real Estate Exam
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Master Article 12-A and NY Real Property Law with our comprehensive study platform. 
                Practice with 557 flashcards covering NY-specific topics and national real estate principles.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/checkout?plan=trial&state=NY" 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                >
                  Start Free Trial
                </Link>
                <Link 
                  to="/demo" 
                  className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-blue-600 hover:text-white transition-all"
                >
                  Try Demo
                </Link>
              </div>
            </div>

            {/* Sample Flashcard */}
            <div className="max-w-md mx-auto mb-16">
              <h3 className="text-2xl font-bold text-center mb-6 text-gray-900">Try a Sample Question</h3>
              <FlashcardCard
                card={sampleCard}
                onCardClick={handleCardClick}
                onSwipe={() => {}}
                showAnswer={showAnswer}
                isAnimating={false}
              />
            </div>
          </div>
        </section>

        {/* Exam Structure */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">New York Real Estate Exam Structure</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The New York real estate salesperson exam has 75 multiple-choice questions in 90 minutes. 
                Approximately 60% cover national principles and 40% cover New York-specific laws (Article 12-A).
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* National Content */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-blue-900 mb-6">National Content (~45 questions)</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Agency & Fiduciary Duties</span>
                    <span className="text-blue-600 font-semibold">~12 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Contracts</span>
                    <span className="text-blue-600 font-semibold">~9 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Finance & Mortgages</span>
                    <span className="text-blue-600 font-semibold">~9 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Property Ownership & Land Use</span>
                    <span className="text-blue-600 font-semibold">~9 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Property Valuation</span>
                    <span className="text-blue-600 font-semibold">~7 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Fair Housing & Ethics</span>
                    <span className="text-blue-600 font-semibold">~6 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Real Estate Math</span>
                    <span className="text-blue-600 font-semibold">~6 questions</span>
                  </div>
                </div>
              </div>

              {/* NY State Content */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-amber-900 mb-6">New York State Content (~30 questions)</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-amber-800">Article 12-A & License Law</span>
                    <span className="text-amber-600 font-semibold">~8 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-amber-800">NY Agency Disclosures</span>
                    <span className="text-amber-600 font-semibold">~6 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-amber-800">NY Contracts & Forms</span>
                    <span className="text-amber-600 font-semibold">~6 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-amber-800">Continuing Education</span>
                    <span className="text-amber-600 font-semibold">~4 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-amber-800">Broker/Salesperson Duties</span>
                    <span className="text-amber-600 font-semibold">~4 questions</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-amber-800">NY-Specific Regulations</span>
                    <span className="text-amber-600 font-semibold">~2 questions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Passing Requirements */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Passing Requirements</h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                You need 70% (53 correct out of 75) to pass the New York real estate salesperson exam.
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">70% Passing Score</h3>
                <div className="text-4xl font-bold mb-2">53/75</div>
                <p className="text-blue-100">Minimum correct answers required</p>
                <p className="text-sm text-blue-200 mt-2">90-minute time limit • Administered by PSI</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose RealEstart for New York?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our New York-specific content covers Article 12-A, NY license law, agency disclosures, and national principles.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl text-white">📚</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">557 NY Flashcards</h3>
                <p className="text-gray-600">
                  NY-specific topics (Article 12-A, license law, agency) plus national real estate principles.
                </p>
              </div>

              <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl text-white">🎯</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">75 Questions Per Practice Test</h3>
                <p className="text-gray-600">
                  300-question bank: each practice test randomly selects 75 questions, mirroring the NY exam format.
                </p>
              </div>

              <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl text-white">📊</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">Progress Tracking</h3>
                <p className="text-gray-600">
                  Monitor your performance and identify areas that need more study.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6">Ready to Pass Your New York Real Estate Exam?</h2>
            <p className="text-xl mb-8 text-white/90">
              Start studying with 557 flashcards covering NY license law and national principles.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/checkout?plan=trial&state=NY" 
                className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105"
              >
                Start Your Free Trial
              </Link>
              <Link 
                to="/demo" 
                className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-blue-600 transition-all"
              >
                Try Demo First
              </Link>
            </div>
            <p className="mt-4 text-sm text-white/70">3-day free trial • No credit card required</p>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="text-2xl font-bold mb-4">RealEstart.io</div>
              <p className="text-gray-400 mb-4">
                Your trusted companion for New York real estate education and licensing preparation.
              </p>
              <p className="text-sm text-gray-500">
                &copy; 2024 RealEstart.io. This is a study aid and is not affiliated with the NY Department of State or any official exam provider.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default NewYorkRealEstateExam;
